import styled from "@emotion/styled"
import { PSelect, PSelectOption } from "@porsche-design-system/components-react"
import type { PSelectProps, SelectChangeEventDetail } from "@porsche-design-system/components-react"
import { spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import type { ForwardedRef, ReactElement } from "react"
import { forwardRef, useCallback, useMemo } from "react"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Tooltip } from "src/components/tooltip/Tooltip"

export interface Option<T extends string = string> {
	hexCode?: string
	label: string
	value: T
}

type SelectValue<T extends string> = T | undefined | null

export interface SelectProps<T extends string = string> {
	description?: string
	disabledHint?: string
	emptyLabel?: string
	emptyValue?: T
	error?: string
	filter?: boolean
	label?: string
	name: string
	onChange?: (value: T | null) => void
	options: Option<T>[]
	required?: boolean
	value?: SelectValue<T>
}

const SelectInner = <T extends string = string>(
	props: SelectProps<T>,
	ref: ForwardedRef<HTMLSelectElement>,
): ReactElement => {
	const {
		disabledHint,
		emptyLabel,
		emptyValue,
		error,
		filter,
		label,
		name,
		onChange,
		options,
		required,
		value,
		...rest
	} = props

	const handleChange = useCallback(
		(event: CustomEvent<SelectChangeEventDetail>) => {
			if (onChange) {
				const val = event.detail.value as T | null
				onChange(val === emptyPlaceholder ? null : val)
			}
		},
		[onChange],
	)

	const componentProps: PSelectProps = {
		label,
		message: error,
		name,
		state: error ? "error" : undefined,
	}

	const emptyVal = useMemo(() => emptyValue ?? (emptyPlaceholder as T), [emptyValue])
	const hasValueOption = useMemo(
		() => typeof value === "string" && options.some((option) => option.value === value),
		[options, value],
	)

	const selectProps = useMemo(
		() => ({
			disabled: !!disabledHint,
			name,
			ref,
			required,
			value: hasValueOption ? value : emptyVal,
		}),
		[disabledHint, name, ref, required, value, emptyVal, hasValueOption],
	)

	const hasEmptyOption = useMemo(
		() => !required || !value || value === emptyVal || !hasValueOption,
		[required, value, emptyVal, hasValueOption],
	)

	return (
		<Tooltip content={disabledHint}>
			<PSelect {...rest} {...componentProps} {...selectProps} onChange={handleChange} filter={filter}>
				{hasEmptyOption && <PSelectOption value={emptyVal}>{emptyLabel ?? emptyPlaceholder}</PSelectOption>}
				{options.map((o, index) => (
					<PSelectOption key={index} value={o.value}>
						{o.hexCode ? (
							<ColorOption>
								<ColorTile $color={o.hexCode} />
								{o.label}
							</ColorOption>
						) : (
							o.label
						)}
					</PSelectOption>
				))}
			</PSelect>
		</Tooltip>
	)
}

export const Select = forwardRef(SelectInner) as (<T extends string = string>(
	props: SelectProps<T> & { ref?: ForwardedRef<HTMLSelectElement> },
) => ReactElement) & { displayName?: string }
Select.displayName = "Select"

const ColorOption = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacingStaticSm};
`

const ColorTile = styled("div", { shouldForwardProp })<{ $color: string }>`
	width: 1rem;
	height: 1rem;
	border-radius: 50%;
	background: ${({ $color }) => $color};
`
